/**
 * Playbook Builder API service (Plan 97/B1).
 */

import { apiClient } from './client';
import type {
  Playbook,
  PlaybookListItem,
  PlaybookCreate,
  PlaybookUpdate,
  PlaybookClause,
  PlaybookClauseCreate,
  PlaybookClauseUpdate,
  PlaybookPosition,
  PlaybookPositionCreate,
  PlaybookPositionUpdate,
  PlaybookExplanation,
  PlaybookExplanationUpsert,
  ClauseType,
  ImportSession,
  CancelImportResult,
  DeIdSettings,
  ImportConfirmRequest,
  ImportConfirmResponse,
  ImportConflict,
  AssistRequest,
  AssistResponse,
  GenerateFieldRequest,
  GenerateFieldResponse,
  PositionAnnotation,
  PositionAnnotationCreate,
  PositionAnnotationUpdate,
} from '../../types/playbook';

const BASE = '/api/v1';

// Plan 196: response shape of GET /api/v1/playbooks/positions/{id}.
// Bundled join (position + clause + optional explanation + playbook) so
// the negotiation right-pane attribution block can render in one fetch.
export interface PositionAttributionResponse {
  position: {
    id: string;
    position_type: 'house' | 'first_fallback' | 'non_negotiable';
    clause_text: string | null;
    version_label: string | null;
    notes: unknown;  // JSONB — shape varies; renderNotes() handles defensively
  };
  clause: {
    id: string;
    name: string;
    clause_type: string | null;
  };
  explanation: {
    reasoning: string | null;
    external_comment: string | null;
    key_features: unknown;  // JSONB
  } | null;
  playbook: {
    id: string;
    title: string;
    stance: 'vendor' | 'customer' | 'neutral';
  };
}

class PlaybookAPI {
  // ------------------------------------------------------------------
  // Playbooks
  // ------------------------------------------------------------------

  /** List all user's playbooks. */
  async listPlaybooks(): Promise<PlaybookListItem[]> {
    const { data } = await apiClient.get<PlaybookListItem[]>(`${BASE}/playbooks`);
    return data;
  }

  /** Create a new playbook. */
  async createPlaybook(payload: PlaybookCreate): Promise<PlaybookListItem> {
    const { data } = await apiClient.post<PlaybookListItem>(`${BASE}/playbooks`, payload);
    return data;
  }

  /** Get a playbook with nested clauses, positions, and explanations. */
  async getPlaybook(id: string): Promise<Playbook> {
    const { data } = await apiClient.get<Playbook>(`${BASE}/playbooks/${id}`);
    return data;
  }

  /** Update playbook metadata. */
  async updatePlaybook(id: string, payload: PlaybookUpdate): Promise<PlaybookListItem> {
    const { data } = await apiClient.put<PlaybookListItem>(`${BASE}/playbooks/${id}`, payload);
    return data;
  }

  /** Delete a playbook (cascades). */
  async deletePlaybook(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/playbooks/${id}`);
  }

  // ------------------------------------------------------------------
  // Clauses
  // ------------------------------------------------------------------

  /** Add a clause to a playbook. */
  async createClause(playbookId: string, payload: PlaybookClauseCreate): Promise<PlaybookClause> {
    const { data } = await apiClient.post<PlaybookClause>(
      `${BASE}/playbooks/${playbookId}/clauses`,
      payload,
    );
    return data;
  }

  /** Update a clause. */
  async updateClause(
    playbookId: string,
    clauseId: string,
    payload: PlaybookClauseUpdate,
  ): Promise<PlaybookClause> {
    const { data } = await apiClient.put<PlaybookClause>(
      `${BASE}/playbooks/${playbookId}/clauses/${clauseId}`,
      payload,
    );
    return data;
  }

  /** Delete a clause (cascades positions and explanation). */
  async deleteClause(playbookId: string, clauseId: string): Promise<void> {
    await apiClient.delete(`${BASE}/playbooks/${playbookId}/clauses/${clauseId}`);
  }

  /** Reorder clauses. */
  async reorderClauses(playbookId: string, clauseIds: string[]): Promise<void> {
    await apiClient.put(`${BASE}/playbooks/${playbookId}/clauses/_/reorder`, {
      clause_ids: clauseIds,
    });
  }

  // ------------------------------------------------------------------
  // Positions
  // ------------------------------------------------------------------

  /** Add a position to a clause. */
  async createPosition(
    playbookId: string,
    clauseId: string,
    payload: PlaybookPositionCreate,
  ): Promise<PlaybookPosition> {
    const { data } = await apiClient.post<PlaybookPosition>(
      `${BASE}/playbooks/${playbookId}/clauses/${clauseId}/positions`,
      payload,
    );
    return data;
  }

  /** Update a position. */
  async updatePosition(positionId: string, payload: PlaybookPositionUpdate): Promise<PlaybookPosition> {
    const { data } = await apiClient.put<PlaybookPosition>(
      `${BASE}/playbooks/positions/${positionId}`,
      payload,
    );
    return data;
  }

  /** Delete a position. */
  async deletePosition(positionId: string): Promise<void> {
    await apiClient.delete(`${BASE}/playbooks/positions/${positionId}`);
  }

  // ── Position Annotations ───────────────────────────────────────────

  async createAnnotation(
    positionId: string,
    payload: PositionAnnotationCreate,
  ): Promise<PositionAnnotation> {
    const { data } = await apiClient.post<PositionAnnotation>(
      `${BASE}/playbooks/positions/${positionId}/annotations`,
      payload,
    );
    return data;
  }

  async updateAnnotation(
    annotationId: string,
    payload: PositionAnnotationUpdate,
  ): Promise<PositionAnnotation> {
    const { data } = await apiClient.put<PositionAnnotation>(
      `${BASE}/playbooks/annotations/${annotationId}`,
      payload,
    );
    return data;
  }

  async deleteAnnotation(annotationId: string): Promise<void> {
    await apiClient.delete(`${BASE}/playbooks/annotations/${annotationId}`);
  }

  // ------------------------------------------------------------------
  // Explanations
  // ------------------------------------------------------------------

  /** Create or update clause explanation. */
  async upsertExplanation(
    playbookId: string,
    clauseId: string,
    payload: PlaybookExplanationUpsert,
  ): Promise<PlaybookExplanation> {
    const { data } = await apiClient.put<PlaybookExplanation>(
      `${BASE}/playbooks/${playbookId}/clauses/${clauseId}/explanation`,
      payload,
    );
    return data;
  }

  // ------------------------------------------------------------------
  // Clause Types
  // ------------------------------------------------------------------

  /** List all clause types from taxonomy. */
  async listClauseTypes(): Promise<ClauseType[]> {
    const { data } = await apiClient.get<ClauseType[]>(`${BASE}/clause-types`);
    return data;
  }

  // ------------------------------------------------------------------
  // Import (wired in Task Group C)
  // ------------------------------------------------------------------

  /** Step 1: Upload document and start AI extraction. Returns session with status='extracting'. */
  async extractImportCandidates(
    playbookId: string,
    file: File,
  ): Promise<ImportSession> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post<ImportSession>(
      `${BASE}/playbooks/${playbookId}/import/extract`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000 },
    );
    return data;
  }

  /** Poll import session status until extraction completes. */
  async getImportSession(
    playbookId: string,
    sessionId: string,
    opts?: { signal?: AbortSignal },
  ): Promise<ImportSession> {
    const { data } = await apiClient.get<ImportSession>(
      `${BASE}/playbooks/${playbookId}/import/session?import_session_id=${sessionId}`,
      { signal: opts?.signal },
    );
    return data;
  }

  /** Get latest pending import session for a playbook (for resume after refresh). */
  /**
   * Discard a running import (plan 242 / #984).
   *
   * The durable guarantee is that the results are discarded. Extraction itself
   * may run to completion on another replica -- see the endpoint docstring.
   */
  async cancelImportExtraction(
    playbookId: string,
    importSessionId: string,
  ): Promise<CancelImportResult> {
    const { data } = await apiClient.post<CancelImportResult>(
      `${BASE}/playbooks/${playbookId}/import/cancel`,
      null,
      { params: { import_session_id: importSessionId } },
    );
    return data;
  }

  async getLatestImportSession(playbookId: string): Promise<ImportSession | null> {
    const { data } = await apiClient.get<ImportSession | null>(
      `${BASE}/playbooks/${playbookId}/import/latest-session`,
    );
    return data;
  }

  /** Step 2-3: Run de-identification on selected candidates. */
  async deidentifyCandidates(
    playbookId: string,
    importSessionId: string,
    settings: DeIdSettings,
  ): Promise<ImportSession> {
    const { data } = await apiClient.post<ImportSession>(
      `${BASE}/playbooks/${playbookId}/import/deid?import_session_id=${importSessionId}`,
      settings,
      { timeout: 300_000 },
    );
    return data;
  }

  /** Step 3: Classify candidates. */
  async classifyCandidates(
    playbookId: string,
    importSessionId: string,
  ): Promise<ImportSession & { conflicts?: ImportConflict[] }> {
    const { data } = await apiClient.post<ImportSession & { conflicts?: ImportConflict[] }>(
      `${BASE}/playbooks/${playbookId}/import/classify?import_session_id=${importSessionId}`,
      {},
    );
    return data;
  }

  /** Step 4: Confirm import with conflict resolutions. */
  async confirmImport(
    playbookId: string,
    importSessionId: string,
    payload: ImportConfirmRequest,
  ): Promise<ImportConfirmResponse> {
    const { data } = await apiClient.post<ImportConfirmResponse>(
      `${BASE}/playbooks/${playbookId}/import/confirm?import_session_id=${importSessionId}`,
      payload,
      { timeout: 300_000 },
    );
    return data;
  }

  // ------------------------------------------------------------------
  // Matter-Playbook Associations
  // ------------------------------------------------------------------

  async listMatterPlaybooks(matterId: string): Promise<PlaybookListItem[]> {
    const { data } = await apiClient.get<PlaybookListItem[]>(
      `${BASE}/matters/${matterId}/playbooks`,
    );
    return data;
  }

  // Plan 196: single joined fetch for the negotiation right-pane
  // attribution block. Returns position + clause + explanation +
  // playbook in one round trip; ownership enforced server-side.
  async getPositionAttribution(positionId: string): Promise<PositionAttributionResponse> {
    const { data } = await apiClient.get<PositionAttributionResponse>(
      `${BASE}/playbooks/positions/${positionId}`,
    );
    return data;
  }

  async attachToMatter(matterId: string, playbookId: string): Promise<void> {
    await apiClient.post(`${BASE}/matters/${matterId}/playbooks/${playbookId}`);
  }

  async detachFromMatter(matterId: string, playbookId: string): Promise<void> {
    await apiClient.delete(`${BASE}/matters/${matterId}/playbooks/${playbookId}`);
  }

  // ------------------------------------------------------------------
  // Glyphe Assist (wired in Task Group D)
  // ------------------------------------------------------------------

  /** Improve a clause description for better AI generation. */
  async improveAssistPrompt(
    playbookId: string,
    clauseId: string,
    payload: AssistRequest,
  ): Promise<{ improved_description: string }> {
    const { data } = await apiClient.post<{ improved_description: string }>(
      `${BASE}/playbooks/${playbookId}/clauses/${clauseId}/assist/improve`,
      payload,
    );
    return data;
  }

  /** Generate content for a specific position field (reasoning, external_comment, clause_text). */
  async generateField(
    playbookId: string,
    clauseId: string,
    payload: GenerateFieldRequest,
  ): Promise<GenerateFieldResponse> {
    const { data } = await apiClient.post<GenerateFieldResponse>(
      `${BASE}/playbooks/${playbookId}/clauses/${clauseId}/assist/generate-field`,
      payload,
    );
    return data;
  }

  /** Generate clause variants from description. */
  async generateAssist(
    playbookId: string,
    clauseId: string,
    payload: AssistRequest,
  ): Promise<AssistResponse> {
    const { data } = await apiClient.post<AssistResponse>(
      `${BASE}/playbooks/${playbookId}/clauses/${clauseId}/assist`,
      payload,
    );
    return data;
  }
}

export const playbookApi = new PlaybookAPI();
